/**
 * Precio de una ruta del tarifario (reescrito en JSX legible el 2026-10-08).
 * Desde / hasta / tipo salen de listas — no se escriben a mano — para que el
 * precio se encuentre solo al cerrar una orden:
 *  - Desde: una sede (ICA = desde cualquier fundo de Ica) o un fundo puntual,
 *    que gana sobre la sede;
 *  - Hasta: los destinos de la lista (Configuración → Movilidad);
 *  - Tipo: los tipos de la flota.
 */
import {
  rec_Lo as claseEntrada,
  rec_MarcaCarga as MarcaCarga,
  rec_Modal as Modal,
  rec_React as React,
  rec_Ro as clasePrimario,
  rec_api as api,
  rec_zo as claseSecundario,
} from "../../runtime/index.jsx";

// runtime/index.jsx importa este archivo antes de definir React (import circular).
const useEffect = (...a) => React.useEffect(...a);
const useState = (...a) => React.useState(...a);

const TIPOS_FIJOS = [`AUTO`, `AUTO 6-7`, `COMBI/VAN`, `COUSTER`, `MINIBUS`, `BUS`];

/** Agrega el valor actual si no está en la lista (rutas viejas escritas a mano). */
const conActual = (lista, actual) => (actual && !lista.includes(actual) ? [actual, ...lista] : lista);

export function FormularioRuta({ editar, onClose, onGuardado }) {
  const [origen, setOrigen] = useState(editar?.origen ?? `ICA`);
  const [destino, setDestino] = useState(editar?.destino ?? ``);
  const [tipo, setTipo] = useState(editar?.tipo ?? ``);
  const [precio, setPrecio] = useState(editar?.precio ?? ``);
  const [minutos, setMinutos] = useState(editar?.minutosViaje ?? ``);
  const [activo, setActivo] = useState(!editar || editar.activo);
  const [error, setError] = useState(``);
  const [guardando, setGuardando] = useState(false);
  const [listas, setListas] = useState({ sedes: [`ICA`], fundos: [], destinos: [], tipos: TIPOS_FIJOS });

  useEffect(() => {
    Promise.all([
      api(`/transporte/destinos`).catch(() => []),
      api(`/transporte/unidades`).catch(() => []),
      api(`/fundos`).catch(() => []),
    ]).then(([destinos, unidades, fundos]) => {
      const tipos = [...new Set([...TIPOS_FIJOS, ...unidades.map((u) => String(u.tipo ?? ``).toUpperCase()).filter(Boolean)])];
      const sedes = [...new Set(fundos.map((f) => f.sedeNombre).filter(Boolean))];
      setListas({
        sedes: sedes.length ? sedes : [`ICA`],
        fundos: fundos.filter((f) => f.activo).map((f) => f.nombre),
        destinos: destinos.filter((d) => d.activo).map((d) => d.nombre),
        tipos,
      });
    });
  }, []);

  async function guardar(e) {
    e.preventDefault();
    if (guardando) return;
    setGuardando(true);
    setError(``);
    try {
      await api(`/transporte/tarifario`, {
        method: `POST`,
        body: JSON.stringify({
          id: editar?.id,
          origen,
          destino,
          tipo,
          precio: precio === `` ? null : Number(precio),
          minutosViaje: minutos === `` ? null : Number(minutos),
          activo,
        }),
      });
      onGuardado();
    } catch (x) {
      setError(x.message);
    } finally {
      setGuardando(false);
    }
  }

  const origenes = conActual([...listas.sedes, ...listas.fundos], origen);
  return (
    <Modal titulo={editar ? `${editar.origen} → ${editar.destino}` : `Nueva ruta`} onClose={onClose}>
      <form onSubmit={guardar} aria-label="Precio de la ruta" className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm text-(--text)">
            Desde
            <select className={claseEntrada} value={origen} onChange={(e) => setOrigen(e.target.value)} required>
              <optgroup label="Sede (cualquier fundo)">
                {conActual(listas.sedes, listas.sedes.includes(origen) || listas.fundos.includes(origen) ? `` : origen).map((s) => <option key={s} value={s}>{s}</option>)}
              </optgroup>
              {listas.fundos.length > 0 && (
                <optgroup label="Un fundo puntual">
                  {listas.fundos.map((f) => <option key={f} value={f}>{f}</option>)}
                </optgroup>
              )}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-(--text)">
            Hasta
            <select className={claseEntrada} value={destino} onChange={(e) => setDestino(e.target.value)} required>
              <option value="" disabled>Elige el destino</option>
              {conActual(listas.destinos, destino).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-(--text)">
            Tipo de unidad
            <select className={claseEntrada} value={tipo} onChange={(e) => setTipo(e.target.value)} required>
              <option value="" disabled>Elige el tipo</option>
              {conActual(listas.tipos, tipo).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-(--text)">
            Precio (S/)
            <input className={claseEntrada} type="number" min="0" step="0.01" value={precio} onChange={(e) => setPrecio(e.target.value)} required />
          </label>
        </div>
        <p className="text-xs text-(--text)">
          «ICA» vale para cualquier fundo de Ica; si cargas un precio para un fundo puntual, ese gana. El precio va por tipo de unidad.
        </p>
        <label className="flex flex-col gap-1 text-sm text-(--text)">
          Cuánto dura el viaje (minutos)
          <input className={claseEntrada} type="number" min="0" placeholder="45" value={minutos} onChange={(e) => setMinutos(e.target.value)} />
          <span className="text-xs">Con esto se sabe a qué hora llega la unidad y cuándo preguntarle a la gente cómo le fue.</span>
        </label>
        {editar && <MarcaCarga checked={activo} onChange={setActivo}>Se propone al cerrar una orden</MarcaCarga>}
        {error && <p className="text-sm text-(--error)">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={claseSecundario}>Cancelar</button>
          <button type="submit" disabled={guardando} className={clasePrimario}>{guardando ? `Guardando…` : `Guardar`}</button>
        </div>
      </form>
    </Modal>
  );
}
